import Link, { LinkProps } from "next/link";
import { forwardRef, HTMLAttributes } from "react";

import { Button } from "@repo/ui/button";
import * as Flex from "@repo/ui/flex";

import classes from "./pagination.module.css";

export type PaginationButtonProps = HTMLAttributes<HTMLButtonElement> & {
  linkProps: LinkProps;
  asChild?: boolean;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
};

export type PaginationProps = Flex.FlexContainerProps & {
  pages: {
    number: number;
    key: string;
    query: { [k: string]: string };
  }[];
  threshold?: number;
  adjacentCount?: number;
  currentPage: number;
  prefetching?: boolean;
  itemProps?: HTMLAttributes<HTMLButtonElement>;
};

const Ellipsis = () => (
  <Flex.Item align="center" asChild>
    <span aria-hidden="true" className={classes.ellipses}>
      ...
    </span>
  </Flex.Item>
);

const PaginationButton = forwardRef<HTMLButtonElement, PaginationButtonProps>(
  (
    {
      onClick,
      className,
      children,
      linkProps,
      asChild = true,
      disabled,
      ...props
    },
    ref,
  ) => {
    return (
      <Button
        ref={ref}
        disabled={disabled}
        onClick={onClick}
        asChild={asChild}
        className={className}
        aria-label={`Page ${children}`}
        {...props}
      >
        <Link {...linkProps}>{children}</Link>
      </Button>
    );
  },
);

export const Previous = forwardRef<HTMLButtonElement, PaginationButtonProps>(
  (props, ref) => (
    <PaginationButton ref={ref} aria-label="Previous page" {...props} />
  ),
);

Previous.displayName = "Previous";

export const Next = forwardRef<HTMLButtonElement, PaginationButtonProps>(
  (props, ref) => (
    <PaginationButton ref={ref} aria-label="Next page" {...props} />
  ),
);

Next.displayName = "Next";

export const Pagination = forwardRef<HTMLDivElement, PaginationProps>(
  (
    {
      pages,
      prefetching,
      currentPage,
      itemProps,
      threshold = 7,
      adjacentCount = 1,
      ...props
    },
    ref,
  ) => {
    const shouldShowEllipsis = pages.length >= threshold;

    const getVisiblePages = () => {
      if (!shouldShowEllipsis) {
        return pages.slice(1, -1);
      }

      const currentPageIndex = pages.findIndex(
        (page) => page.number === currentPage,
      );

      if (currentPageIndex <= adjacentCount + 1) {
        return pages.slice(1, adjacentCount * 2 + 2);
      }

      if (currentPageIndex >= pages.length - (adjacentCount + 2)) {
        return pages.slice(-(adjacentCount * 2) - 2, -1);
      }

      return pages.slice(
        currentPageIndex - adjacentCount,
        currentPageIndex + adjacentCount + 1,
      );
    };

    const visiblePages = getVisiblePages();

    const showStartEllipsis =
      shouldShowEllipsis && currentPage > adjacentCount + 2;
    const showEndEllipsis =
      shouldShowEllipsis && currentPage < pages.length - (adjacentCount + 1);

    return (
      <Flex.Container ref={ref} justify="center" gap="md" {...props}>
        {showStartEllipsis && <Ellipsis />}

        {visiblePages.map(({ number, key, query }) => {
          const isCurrentPage = currentPage === number;
          const isAdjacentPage =
            currentPage === number - 1 || currentPage === number + 1;

          return (
            <Flex.Item key={key} asChild>
              <PaginationButton
                linkProps={{
                  href: { pathname: null, query },
                  prefetch: prefetching ? isAdjacentPage : null,
                }}
                aria-current={isCurrentPage ? "page" : undefined}
                aria-label={`${isCurrentPage ? "Current page, page" : "Page"} ${number}`}
                data-current-page={isCurrentPage}
                {...itemProps}
              >
                {number}
              </PaginationButton>
            </Flex.Item>
          );
        })}

        {showEndEllipsis && <Ellipsis />}
      </Flex.Container>
    );
  },
);

Pagination.displayName = "Pagination";

export const First = forwardRef<HTMLButtonElement, PaginationButtonProps>(
  (props, ref) => (
    <PaginationButton ref={ref} aria-label="First page" {...props} />
  ),
);
First.displayName = "First";

export const Last = forwardRef<HTMLButtonElement, PaginationButtonProps>(
  (props, ref) => (
    <PaginationButton ref={ref} aria-label="Last page" {...props} />
  ),
);

Last.displayName = "Last";
